import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sun, Moon, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { useTheme } from '../../context/ThemeContext';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { 
        name: 'Services', 
        path: '/services',
        children: [
            { name: 'GMB Optimization', path: '/gmb-optimization-services' },
            { name: 'GEO SEO', path: '/geo-seo-services' },
            { name: 'AI Search Optimization', path: '/ai-search-optimization' },
            { name: 'High-Intent SEO', path: '/seo-services' },
            { name: 'Programmatic SEO', path: '/programmatic-seo' },
        ]
    },
    { name: 'Case Studies', path: '/case-studies' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
];

export const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { theme, toggleTheme } = useTheme();
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setIsOpen(false);
    }, [location]);

    return (
        <header 
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled 
                    ? 'bg-background/80 backdrop-blur-xl border-b border-border shadow-sm' 
                    : 'bg-transparent'
            }`}
            data-testid="navbar"
        >
            <nav className="container-custom">
                <div className="flex items-center justify-between h-16 md:h-20">
                    {/* Logo */}
                    <Link 
                        to="/" 
                        className="flex items-center gap-2 font-bold text-xl md:text-2xl"
                        data-testid="logo-link"
                    >
                        <span className="text-gradient-primary font-['Outfit']">Aitrixy</span>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden lg:flex items-center gap-1">
                        {navLinks.map((link) => (
                            link.children ? (
                                <DropdownMenu key={link.name}>
                                    <DropdownMenuTrigger asChild>
                                        <button 
                                            className={`nav-link px-4 py-2 text-sm font-medium inline-flex items-center gap-1 rounded-lg hover:bg-muted/50 transition-colors ${
                                                location.pathname.includes('seo') || location.pathname.includes('services')
                                                    ? 'text-primary' 
                                                    : 'text-muted-foreground hover:text-foreground'
                                            }`}
                                            data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                                        >
                                            {link.name}
                                            <ChevronDown className="w-4 h-4" />
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="start" className="w-56">
                                        <DropdownMenuItem asChild>
                                            <Link to="/services" className="w-full font-medium">
                                                All Services
                                            </Link>
                                        </DropdownMenuItem>
                                        {link.children.map((child) => (
                                            <DropdownMenuItem key={child.path} asChild>
                                                <Link to={child.path} className="w-full">
                                                    {child.name}
                                                </Link>
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            ) : (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    className={`nav-link px-4 py-2 text-sm font-medium rounded-lg hover:bg-muted/50 transition-colors ${
                                        location.pathname === link.path 
                                            ? 'text-primary' 
                                            : 'text-muted-foreground hover:text-foreground'
                                    }`}
                                    data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                                >
                                    {link.name}
                                </Link>
                            )
                        ))}
                    </div>

                    {/* Right Side Actions */}
                    <div className="flex items-center gap-2 md:gap-4">
                        {/* Theme Toggle */}
                        <button
                            onClick={toggleTheme}
                            className="p-2 rounded-lg hover:bg-muted/50 transition-colors"
                            aria-label="Toggle theme"
                            data-testid="theme-toggle"
                        >
                            {theme === 'light' ? (
                                <Moon className="w-5 h-5" />
                            ) : (
                                <Sun className="w-5 h-5" />
                            )}
                        </button>

                        {/* CTA Button */}
                        <Button 
                            asChild 
                            className="hidden md:inline-flex btn-glow rounded-full px-6"
                            data-testid="header-cta-btn"
                        >
                            <Link to="/contact">Book Strategy Call</Link>
                        </Button>

                        {/* Mobile Menu Toggle */}
                        <button
                            onClick={() => setIsOpen(!isOpen)}
                            className="lg:hidden p-2 rounded-lg hover:bg-muted/50 transition-colors"
                            aria-label="Toggle menu"
                            data-testid="mobile-menu-toggle"
                        >
                            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>

                {/* Mobile Navigation */}
                {isOpen && (
                    <div className="lg:hidden absolute top-full left-0 right-0 bg-background border-b border-border shadow-lg animate-slide-down">
                        <div className="container-custom py-4 space-y-1">
                            {navLinks.map((link) => (
                                link.children ? (
                                    <div key={link.name} className="space-y-1">
                                        <Link
                                            to={link.path}
                                            className="block px-4 py-3 text-base font-medium text-foreground rounded-lg hover:bg-muted/50"
                                        >
                                            {link.name}
                                        </Link>
                                        <div className="pl-4 space-y-1">
                                            {link.children.map((child) => (
                                                <Link
                                                    key={child.path}
                                                    to={child.path}
                                                    className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted/50"
                                                >
                                                    {child.name}
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <Link
                                        key={link.path}
                                        to={link.path}
                                        className={`block px-4 py-3 text-base font-medium rounded-lg hover:bg-muted/50 ${
                                            location.pathname === link.path 
                                                ? 'text-primary bg-primary/5' 
                                                : 'text-foreground'
                                        }`}
                                    >
                                        {link.name}
                                    </Link>
                                )
                            ))}
                            <div className="pt-4 px-4">
                                <Button asChild className="w-full btn-glow rounded-full">
                                    <Link to="/contact">Book Strategy Call</Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </nav>
        </header>
    );
};
